import React, { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import CircleStyle from 'ol/style/Circle';
import Text from 'ol/style/Text';
import { fromLonLat } from 'ol/proj';
import { isEmpty as isEmptyExtent } from 'ol/extent';
import 'ol/ol.css';

// A region's shape and what it catches. Read-only: the shape is produced by
// seeding from appellations and tuning the buffer, and whether hand-editing is
// needed at all is an open question. See docs/PUBLIC_SITE_EXPLORE_PLAN.md.
//
// Pins are clustered because a region holds hundreds (337 in the one trialled)
// and unclustered they are an unreadable mass at the zoom a whole region fits
// into.

const WINE = '#670801';
const CLUSTER_FILL = '#a5603a';
const CLUSTER_DISTANCE = 44;

const boundaryStyle = new Style({
  fill: new Fill({ color: 'rgba(103, 8, 1, 0.08)' }),
  stroke: new Stroke({ color: WINE, width: 2 }),
});

// Cached by count: hundreds of features get restyled on every pan.
const styleCache = Object.create(null);

const pinStyle = (count) => {
  const key = String(count);
  if (styleCache[key]) return styleCache[key];
  const single = count === 1;
  const style = new Style({
    image: new CircleStyle({
      radius: single ? 6 : Math.min(20, 11 + Math.round(Math.log10(count) * 7)),
      fill: new Fill({ color: single ? WINE : CLUSTER_FILL }),
      stroke: new Stroke({ color: '#ffffff', width: single ? 1.5 : 2 }),
    }),
    text: single ? undefined : new Text({
      text: String(count),
      fill: new Fill({ color: '#ffffff' }),
      font: '600 11px system-ui, sans-serif',
    }),
  });
  styleCache[key] = style;
  return style;
};

export default function RegionBoundaryMap({ boundary, pins = [], height = '460px' }) {
  const elementRef = useRef(null);

  useEffect(() => {
    if (!elementRef.current || !boundary) return undefined;

    const boundarySource = new VectorSource({
      // GeoJSON arrives in EPSG:4326; the map renders in EPSG:3857.
      features: new GeoJSON().readFeatures(boundary, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }),
    });

    const pinSource = new VectorSource({
      features: pins
        .filter((p) => typeof p.lng === 'number' && typeof p.lat === 'number')
        .map((p) => {
          const f = new Feature({ geometry: new Point(fromLonLat([p.lng, p.lat])) });
          f.set('name', p.name);
          return f;
        }),
    });

    const map = new Map({
      target: elementRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({ source: boundarySource, style: boundaryStyle }),
        new VectorLayer({
          source: new Cluster({ distance: CLUSTER_DISTANCE, source: pinSource }),
          style: (feature) => pinStyle((feature.get('features') || []).length),
        }),
      ],
      view: new View({
        projection: 'EPSG:3857',
        center: [0, 0],
        zoom: 2,
        maxZoom: 19,
        enableRotation: false,
      }),
    });

    // Fitting an empty extent throws, which is how the collection map used to
    // crash on a coordless result.
    const extent = boundarySource.getExtent();
    if (!isEmptyExtent(extent)) {
      map.getView().fit(extent, { padding: [24, 24, 24, 24], maxZoom: 13 });
    }

    return () => { map.setTarget(undefined); };
  }, [boundary, pins]);

  if (!boundary) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 border border-dashed border-gray-300 rounded-md text-sm text-gray-500"
        style={{ height }}
      >
        No shape yet. Seed one from the appellations this region covers.
      </div>
    );
  }

  return <div ref={elementRef} style={{ height }} className="rounded-md overflow-hidden border border-gray-200" />;
}
